import { Button } from 'primereact/button';
import { Checkbox } from 'primereact/checkbox';
import { InputText } from 'primereact/inputtext';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import CoachingToDo from '../../../../../components/CoachingToDo/CoachingToDo';
import { AGENTROLE, MANAGERROLE, ROLES, SUPERVISORROLE } from '../../../../../constants';
import { selectCurrentEmpid } from '../../../../../pages/login/authSlice';
import { changeDateFormate, datetimeToDate } from '../../../../../utils/dateFormat';
import { useCoachingGoalSettingMutation } from '../../goalSlice';
import { GoalExpansionTemplateProps } from '../types';
import { checkAscGoalDesc, checkTlGoalDesc, createGoalSettingPayload } from '../validateHelperFunctions';
import DescriptionBox from '../../../../../components/descriptionBox/DescriptionBox';

const findAuthorRole = (level: string) => {
    if (ROLES.Agents?.includes(level)) return AGENTROLE.charAt(0).toUpperCase() + AGENTROLE.slice(1);
    else if (ROLES.Supervisor?.includes(level))
        return SUPERVISORROLE.charAt(0).toUpperCase() + SUPERVISORROLE.slice(1);
    else if (ROLES.Manager?.includes(level))
        return MANAGERROLE.charAt(0).toUpperCase() + MANAGERROLE.slice(1);
    else return '';
};
const alphanumregex = /^[A-Za-z0-9.,! ]*$/;

export const GoalSetting = (props: GoalExpansionTemplateProps) => {
    const [CoachingGoalSetting] = useCoachingGoalSettingMutation();
    const currEmpId = useSelector(selectCurrentEmpid);
    const isAuthor = props.data?.author_employee_number === currEmpId;
    const authorRole = findAuthorRole(props.data?.author_level);
    const recipienteRole = findAuthorRole(props.data?.recipient_level);
    const [tlGoalDesc, setTLGoalDesc] = useState<string>(props.data?.setting?.goal_description || '');
    const [ascGoalDesc, setAscGoalDesc] = useState<string>(props.data?.setting?.response_description || '');
    const [locked, setLocked] = useState<boolean>(props.data?.goal_locked || false);
    const [discussed, setDiscussed] = useState<boolean>(props.data?.setting?.goal_discussed || false);
    const [target, setTarget] = useState<string>(props.data?.target_goal_value || '');
    const [expectedPayout, setExpectedPayout] = useState<string>(props.data?.setting?.expected_payout || '');
    const [todoVisible, setTodoVisible] = useState<boolean>(false);
    const handleTextChange = (e: any, type: 'tlGoalDesc' | 'ascGoalDesc' | 'target' | 'expectedPayout') => {
        const eventVal = e.target.value;
        if (type === 'ascGoalDesc') setAscGoalDesc(eventVal);
        else if (type === 'tlGoalDesc') setTLGoalDesc(eventVal);
        else if (type === 'target') {
            if ((eventVal.includes('.') && eventVal.split('.')[1].length > 1) || eventVal.includes('-'))
                return;
            setTarget(eventVal);
        } else if (type === 'expectedPayout') {
            if ((eventVal.includes('.') && eventVal.split('.')[1].length > 2) || eventVal.includes('-'))
                return;
            setExpectedPayout(eventVal);
        }
    };
    const validate = () => {
        const errors: string[] = [];
        if (!alphanumregex.test(tlGoalDesc.trim()) || !alphanumregex.test(ascGoalDesc.trim())) {
            errors.push('Please enter the goal description with valid characters');
        }

        if (locked) {
            if (!discussed) {
                errors.push('The goal has to be discussed before locking.');
            }
            checkTlGoalDesc(tlGoalDesc, errors);
            checkAscGoalDesc(ascGoalDesc, errors);
        }
        if (isAuthor) {
            checkTlGoalDesc(tlGoalDesc, errors);
            if (ascGoalDesc.trim().length !== 0 || target !== '' || expectedPayout !== '') {
                checkAscGoalDesc(ascGoalDesc, errors);
            }
        } else {
            checkAscGoalDesc(ascGoalDesc, errors);
        }

        if (errors.length > 0) {
            errors.forEach((error) => toast.error(error));
            return false;
        }

        return true;
    };
    const handleSave = () => {
        if (!validate()) return;
        const payload: any = createGoalSettingPayload(
            props.data,
            expectedPayout,
            target,
            ascGoalDesc,
            tlGoalDesc,
            locked,
            discussed,
            currEmpId
        );
        if (Object.keys(payload).length <= 1) {
            toast.warn('No changes made');
            return;
        }

        // call api
        CoachingGoalSetting(payload)
        .unwrap()
        .then((res: any) => {
            if (res?.data) {
                toast.success('Goal setting saved successfully');
            } else {
                toast.error('Failed to save goal setting');
            }
        })
        .catch(() => {
            toast.error('Failed to save goal setting');
        });
    };
    return (
        <div className="p-2">
            <div className="d-flex justify-content-between">
                <h6>Goal Description/Coaching</h6>
                <h6>Beggining of the Month</h6>
            </div>
            <DescriptionBox
                value={tlGoalDesc}
                responseDescription={tlGoalDesc}
                disabled={props.data.goal_locked || props.data.setting.goal_discussed || !isAuthor}
                limit={200}
                onChange={(value) => {
                    const syntheticEvent = {
                        target: { value },
                    };
                    handleTextChange(syntheticEvent, 'tlGoalDesc');
                }}
            />
            <div className="d-flex justify-content-start mt-3">
                <div className="me-4">
                    <h6>Date</h6>
                    <div className="mt-3">
                        {changeDateFormate(datetimeToDate(props.data.setting.goal_date))}
                    </div>
                </div>
                <div className="me-4">
                    <h6>Role</h6>
                    <div className="mt-3">{authorRole}</div>
                </div>
                <div className="me-4">
                    <h6>Discussed</h6>
                    <Checkbox
                        className="mt-3"
                        checked={discussed}
                        onChange={(e) => setDiscussed(e.target.checked)}
                        disabled={props.data.goal_locked || props.data.setting.goal_discussed || !isAuthor}
                    />
                </div>
                <div className="me-4">
                    <h6>Author</h6>
                    {props.receiverName}
                </div>
            </div>
            <div className="splitSection mt-3 mb-3" />

            <div className="d-flex justify-content-between mt-2">
                <h6>Associate Goal Setting</h6>
                <h6>Beggining of the Month</h6>
            </div>
            <DescriptionBox
                value={ascGoalDesc}
                responseDescription={ascGoalDesc}
                disabled={props.data.goal_locked}
                limit={200}
                onChange={(value) => {
                    const syntheticEvent = {
                        target: { value },
                    };
                    handleTextChange(syntheticEvent, 'ascGoalDesc');
                }}
            />
            <div className="d-flex justify-content-between mt-3">
                <span>
                    <h6>Target</h6>
                    <InputText
                        className="borderRadius8  me-4"
                        value={target}
                        onChange={(e) => handleTextChange(e, 'target')}
                        disabled={props.data.goal_locked}
                        keyfilter="num"
                    />
                </span>
                <span>
                    <h6>Expected Payout</h6>
                    <InputText
                        className="borderRadius8 me-4"
                        value={expectedPayout}
                        onChange={(e) => handleTextChange(e, 'expectedPayout')}
                        disabled={props.data.goal_locked}
                        keyfilter="num"
                    />
                </span>
                <div className="me-4">
                    <h6>Date</h6>
                    <div className="mt-3">
                        {props.data.goal_locked
                            ? changeDateFormate(datetimeToDate(props.data.setting.response_date))
                            : changeDateFormate(datetimeToDate(new Date()))}
                    </div>
                </div>
                <div className="me-4">
                    <h6>Role</h6>
                    <div className="mt-3">{recipienteRole}</div>
                </div>
                <div>
                    <h6>Locked</h6>
                    <Checkbox
                        className="mt-3"
                        checked={locked}
                        onChange={(e) => setLocked(e.target.checked)}
                        disabled={props.data.goal_locked}
                    />
                </div>
            </div>
            <div className="d-flex justify-content-end mt-2">
                <Button
                    label="Cancel"
                    onClick={() => props.handleCancel(props.data.coaching_goal_id)}
                    className="btn-secondary me-2"
                />
                <Button
                    label="Save"
                    onClick={handleSave}
                    className="btn-primary me-2"
                    disabled={props.data.goal_locked}
                />
                <CoachingToDo
                    receiverId={props.receiverId}
                    receiverName={props.receiverName}
                    visible={todoVisible}
                    setVisible={setTodoVisible}
                    coachingEntityTypeId={5}
                    coachingEntityId={props?.goalId}
                    coachingStatusId={1}
                />
            </div>
        </div>
    );
};
